import { useState, useEffect, useRef } from "react";
import { Box, TextField, Typography, FormControlLabel, Checkbox, Button, useMediaQuery, CircularProgress } from "@mui/material";
import { motion } from "framer-motion";
import Navbar from "./Navbar";
import { styled } from "@mui/system";
import emailjs from "emailjs-com";

export default function AboutUs() {
  const [showImage, setShowImage] = useState(false);
  const [loading, setLoading] = useState(true);
  const isSmallScreen = useMediaQuery("(max-width: 768px)");

  const nameRef = useRef();
  const emailRef = useRef();
  const phoneRef = useRef();
  const fromLocationRef = useRef();
  const toLocationRef = useRef();

  useEffect(() => {
    // Simulate image loading delay
    const image1 = new Image();
    const image2 = new Image();

    image1.src = "/main.png";
    image2.src = "/main-big.png";

    image1.onload = image2.onload = () => {
      setTimeout(() => setLoading(false), 1000); // Extra delay for smooth transition
    };

    const interval = setInterval(() => {
      setShowImage((prev) => !prev);
    }, 6000);
    
    return () => clearInterval(interval);
  }, []);

  const handleSubmit = (e) => {
    e.preventDefault();

    emailjs
      .send(
        "service_6bdzeho",
        "template_udt8gtg",
        {
          name: nameRef.current.value,
          email: emailRef.current.value,
          phone: phoneRef.current.value,
          fromLocation: fromLocationRef.current.value,
          toLocation: toLocationRef.current.value,
        },
        "2CKd8PIWzRbxVTbZi"
      )
      .then(
        () => {
          alert("Email sent successfully!");
          nameRef.current.value = "";
          emailRef.current.value = "";
          phoneRef.current.value = "";
          fromLocationRef.current.value = "";
          toLocationRef.current.value = "";
        },
        () => {
          alert("Failed to send email. Please try again.");
        }
      );
  };

  return (
    <>
      {loading ? (
        // 🔹 Show Loading Spinner
        <Box
          sx={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            height: "100vh",
            width: "100%",
            background: "#0F0F10",
          }}
        >
          <CircularProgress size={80} sx={{ color: "#FFC107" }} />
        </Box>
      ) : (
        <Box
          sx={{
            minHeight: "50vh",
            width: "100vw",
            maxWidth: "100%",
            display: "flex",
            background: "linear-gradient(120deg, #1C1C1C, rgb(18, 44, 57), #1C1C1C)",
            overflow: "hidden",
            position: "relative",
            margin: 0,
            padding: 0,
            boxSizing: "border-box",
          }}
        >
          <Navbar />

          {/* LEFT SECTION: Form & Banner Text */}
          <Box
            sx={{
              position: "relative",
              zIndex: 2,
              width: { xs: "100%", md: "40%" },
              padding: { xs: "20vh 5%", md: "20vh 3%" },
            }}
          >
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ duration: 1.2, ease: "easeOut" }}
              style={{
                fontSize: "2rem",
                fontWeight: "bold",
                color: "#fff",
                marginBottom: "20px",
                fontFamily: "'Josefin Sans', sans-serif",
              }}
            >
              Reliable Auto Shipping, Hassle-Free Experience
              <br />
              Contact Us Now!
            </motion.div>

            {/* Form Container */}
            <StyledFormContainer>
              <form onSubmit={handleSubmit}>
                <StyledTextField fullWidth label="Name" variant="outlined" size="small" inputRef={nameRef} />
                <StyledTextField fullWidth label="Email" variant="outlined" size="small" inputRef={emailRef} />
                <StyledTextField fullWidth label="Phone Number" variant="outlined" size="small" inputRef={phoneRef} />
                
                <Typography variant="body2" sx={{ color: "#ddd", fontWeight: "bold", marginTop: "10px" }}>
                  Where is your vehicle now?
                </Typography>
                <StyledTextField fullWidth placeholder="Enter City or Zip" variant="outlined" size="small" inputRef={fromLocationRef} />

                <Typography variant="body2" sx={{ color: "#ddd", fontWeight: "bold", marginTop: "10px" }}>
                  Where are you shipping to?
                </Typography>
                <StyledTextField fullWidth placeholder="Enter City or Zip" variant="outlined" size="small" inputRef={toLocationRef} />

                <FormControlLabel
                  control={<StyledCheckbox />}
                  label={
                    <Typography variant="body2" sx={{ color: "#aaa", fontSize: "12px" }}>
                      I agree to receive SMS messages from Infinity Autoship LLC related to my inquiries, transactions, and
                      promotional offers.
                    </Typography>
                  }
                  sx={{ marginTop: "10px" }}
                />
                <Typography variant="body2" sx={{ color: "#aaa", fontSize: "12px", marginTop: "10px" }}>
                  By submitting this form, you agree to our <span style={{ color: "#FFC107" }}>Privacy Policy</span>.
                </Typography>
                <SubmitButton variant="contained" type="submit">
                  SUBMIT
                </SubmitButton>
              </form>
            </StyledFormContainer>
          </Box>

          {/* RIGHT SECTION: Background Image Transition */}
          <motion.div
            initial={{ width: "60%" }}
            animate={{ width: showImage ? "100%" : "60%" }}
            transition={{ duration: 1, ease: "easeInOut" }}
            style={{
              height: "100%",
              width: isSmallScreen ? "100%" : "60%",
              background: showImage
                ? isSmallScreen
                  ? "url('/main.png') center/cover no-repeat"
                  : "url('/main-big.png') center/cover no-repeat"
                : "linear-gradient(120deg, #1C1C1C, rgb(18, 44, 57), #1C1C1C)",
              position: "absolute",
              right: 0,
              top: 0,
              maxWidth: "100%",
              overflow: "hidden",
            }}
          />
        </Box>
      )}
    </>
  );
}

// 🔹 Styled Components
const StyledFormContainer = styled(Box)({
  width: "80%",
  backgroundColor: "#0F0F10",
  padding: "20px 25px",
  borderRadius: "8px",
  border: "1px solid rgba(255, 255, 255, 0.1)",
  color: "#fff",
  fontFamily: "Arial, sans-serif",
});

const StyledTextField = styled(TextField)({
  marginBottom: "10px",
  backgroundColor: "#111",
  borderRadius: "5px",
  "& label": { color: "#aaa", fontSize: "14px" },
  "& input": { color: "#fff", fontSize: "14px", padding: "10px" },
  "& fieldset": { borderColor: "rgba(255, 255, 255, 0.2)" },
});

const StyledCheckbox = styled(Checkbox)({
  color: "#fff",
  "&.Mui-checked": { color: "#FFC107" },
});

const SubmitButton = styled(Button)({
  backgroundColor: "#FFC107",
  color: "#000",
  fontWeight: "bold",
  fontSize: "14px",
  padding: "10px 0",
  marginTop: "10px",
  width: "100%",
  "&:hover": { backgroundColor: "#e0a800" },
});
